import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Sparkles, ArrowUp, ArrowDown, Minus, Gauge, Flame, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from "recharts";
import { fetchInflation, fetchInflationRead, fetchInflationCommentary, fetchMacro } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";

const TARGET = 2.0;
const METRICS = [
  { key: "headline", label: "Headline CPI", color: "#F59E0B" },
  { key: "core", label: "Core CPI", color: "#38BDF8" },
  { key: "ppi", label: "PPI", color: "#A78BFA" },
];
const ECON_COLOR = { US: "#F59E0B", EU: "#38BDF8", UK: "#A78BFA", JP: "#34D399" };

const levelCls = (v) =>
  v == null ? "text-zinc-500" : v <= TARGET + 0.001 ? "text-pos" : v <= TARGET + 1.0 ? "text-amber-term" : "text-neg";

function ChangeBadge({ change }) {
  if (change == null) return <span className="text-zinc-500 text-[11px]">—</span>;
  const Icon = change > 0 ? ArrowUp : change < 0 ? ArrowDown : Minus;
  const cls = change > 0 ? "text-neg" : change < 0 ? "text-pos" : "text-zinc-500";
  return (
    <span className={`inline-flex items-center text-[11px] tabular-nums ${cls}`}>
      <Icon size={10} strokeWidth={2.5} />{change > 0 ? "+" : ""}{change.toFixed(1)}
    </span>
  );
}

function MetricRow({ label, metric }) {
  return (
    <div className="flex items-center justify-between" data-testid={`page-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      {metric ? (
        <span className="inline-flex items-baseline gap-1.5">
          <span className={`text-sm font-semibold tabular-nums ${levelCls(metric.value)}`}>{metric.value.toFixed(1)}%</span>
          <ChangeBadge change={metric.change} />
        </span>
      ) : (
        <span className="text-xs text-zinc-700 tabular-nums">n/a</span>
      )}
    </div>
  );
}

function mergeMetrics(e) {
  const map = new Map();
  METRICS.forEach(({ key }) => {
    (e[key]?.trend || []).forEach((p) => {
      const row = map.get(p.date) || { date: p.date };
      row[key] = p.value;
      map.set(p.date, row);
    });
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function EconomyPanel({ e }) {
  const data = mergeMetrics(e);
  const commentaryQ = useQuery({
    queryKey: ["inflation-commentary", e.code],
    queryFn: () => fetchInflationCommentary(e.code),
    staleTime: 1000 * 60 * 60,
    retry: false,
  });
  const macroQ = useQuery({
    queryKey: ["macro", e.code],
    queryFn: () => fetchMacro(e.code),
    staleTime: 1000 * 60 * 60,
    retry: false,
  });
  const macroItems = Object.values(macroQ.data?.indicators || {}).filter(Boolean);

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid={`econ-panel-${e.code.toLowerCase()}`}>
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <span className="text-amber-term font-semibold">{e.code}</span> · {e.name}
        </span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600 inline-flex items-center gap-2">
          {e.pipeline && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${e.pipeline.level === "high" ? "text-neg border-red-500/50" : "text-amber-term border-amber-500/40"}`}
              title={`PPI ${e.pipeline.ppi}% vs CPI ${e.pipeline.cpi}% (+${e.pipeline.gap}pp)`}
              data-testid={`pipeline-flag-${e.code.toLowerCase()}`}
            >
              <Flame size={10} strokeWidth={2} className={e.pipeline.level === "high" ? "animate-pulse" : ""} /> Pipeline +{e.pipeline.gap}
            </span>
          )}
          <span className="inline-flex items-center gap-1">{e.central_bank} <Gauge size={10} strokeWidth={1.5} /> tgt {TARGET.toFixed(1)}%</span>
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 px-3 py-2.5 border-b border-zinc-900">
        <MetricRow label="Headline" metric={e.headline} />
        <MetricRow label="Core" metric={e.core} />
        <MetricRow label="PPI" metric={e.ppi} />
      </div>

      <div className="h-[240px] w-full px-2 pt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 14, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#18181B" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} minTickGap={26} />
            <YAxis tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} tickFormatter={(v) => `${v}%`} width={40} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} formatter={(v, n) => [`${v?.toFixed(1)}%`, n]} />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: 1 }} />
            <ReferenceLine y={TARGET} stroke="#52525B" strokeDasharray="4 3" label={{ value: "TGT 2.0%", position: "right", fill: "#71717A", fontSize: 9 }} />
            {METRICS.map((m) => (
              <Line key={m.key} name={m.label} type="monotone" dataKey={m.key} stroke={m.color} strokeWidth={1.7} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {macroItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-3 border-t border-zinc-900" data-testid={`econ-macro-${e.code.toLowerCase()}`}>
          {macroItems.map((m) => {
            const good = m.higher_is_better ? m.change > 0 : m.change < 0;
            const bad = m.higher_is_better ? m.change < 0 : m.change > 0;
            const cls = m.change == null ? "text-zinc-500" : good ? "text-pos" : bad ? "text-neg" : "text-zinc-500";
            const unit = m.unit === "%" ? "%" : m.unit === "k" ? "k" : "";
            return (
              <div key={m.label} className="border border-zinc-800 bg-black p-2">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 leading-tight block">{m.label}</span>
                <span className="text-sm font-semibold tabular-nums text-zinc-100">{m.value}{unit}</span>{" "}
                <span className={`text-[11px] tabular-nums ${cls}`}>{m.change > 0 ? "+" : ""}{m.change}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="px-3 py-2.5 border-t border-zinc-900" data-testid={`econ-commentary-${e.code.toLowerCase()}`}>
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles size={11} strokeWidth={1.5} className="text-amber-term" />
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">AI Read · CPI × News × COT</span>
        </div>
        {commentaryQ.isLoading ? (
          <p className="text-xs text-zinc-500 animate-pulse">Synthesizing…</p>
        ) : commentaryQ.isError ? (
          <p className="text-xs text-zinc-600">Commentary unavailable right now.</p>
        ) : (
          <p className="text-xs text-zinc-200 leading-relaxed">{commentaryQ.data?.commentary}</p>
        )}
      </div>
    </div>
  );
}

function ComparisonTool({ economies }) {
  const [mode, setMode] = useState("cross");
  const [metricKey, setMetricKey] = useState("headline");
  const [selected, setSelected] = useState(economies.slice(0, 2).map((e) => e.code));
  const [singleEcon, setSingleEcon] = useState(economies[0]?.code);

  const codes = economies.map((e) => e.code);
  const toggleEcon = (code) =>
    setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));

  const { data, series } = useMemo(() => {
    const map = new Map();
    let lines = [];
    if (mode === "cross") {
      lines = selected
        .map((code) => economies.find((e) => e.code === code))
        .filter(Boolean)
        .map((e) => ({ key: e.code, label: e.code, color: ECON_COLOR[e.code] || "#A1A1AA" }));
      lines.forEach(({ key }) => {
        const e = economies.find((x) => x.code === key);
        (e?.[metricKey]?.trend || []).forEach((p) => {
          const row = map.get(p.date) || { date: p.date };
          row[key] = p.value;
          map.set(p.date, row);
        });
      });
    } else {
      const e = economies.find((x) => x.code === singleEcon);
      lines = METRICS.filter((m) => e?.[m.key]).map((m) => ({ key: m.key, label: m.label, color: m.color }));
      lines.forEach(({ key }) => {
        (e?.[key]?.trend || []).forEach((p) => {
          const row = map.get(p.date) || { date: p.date };
          row[key] = p.value;
          map.set(p.date, row);
        });
      });
    }
    return { data: Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date)), series: lines };
  }, [mode, metricKey, selected, singleEcon, economies]);

  const pill = (active) =>
    `text-[11px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
      active ? "bg-amber-500/70 text-white border-amber-500" : "text-zinc-500 border-zinc-800 hover:border-zinc-600"
    }`;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="inflation-comparison">
      <div className="px-3 py-2 border-b border-zinc-800 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 mr-1">Comparison</span>
        <button onClick={() => setMode("cross")} className={pill(mode === "cross")} data-testid="cmp-mode-cross">Across economies</button>
        <button onClick={() => setMode("within")} className={pill(mode === "within")} data-testid="cmp-mode-within">Within economy</button>
      </div>

      <div className="px-3 py-2.5 flex flex-wrap items-center gap-2 border-b border-zinc-900">
        {mode === "cross" ? (
          <>
            <span className="text-[11px] uppercase tracking-widest text-zinc-600 mr-1">Metric</span>
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetricKey(m.key)} className={pill(metricKey === m.key)} data-testid={`cmp-metric-${m.key}`}>{m.label}</button>
            ))}
            <span className="text-[11px] uppercase tracking-widest text-zinc-600 ml-3 mr-1">Economies</span>
            {codes.map((c) => (
              <button key={c} onClick={() => toggleEcon(c)} className={pill(selected.includes(c))} data-testid={`cmp-econ-${c.toLowerCase()}`}>{c}</button>
            ))}
          </>
        ) : (
          <>
            <span className="text-[11px] uppercase tracking-widest text-zinc-600 mr-1">Economy</span>
            {codes.map((c) => (
              <button key={c} onClick={() => setSingleEcon(c)} className={pill(singleEcon === c)} data-testid={`cmp-single-${c.toLowerCase()}`}>{c}</button>
            ))}
          </>
        )}
      </div>

      <div className="h-[300px] w-full px-2 pt-3 pb-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#18181B" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} minTickGap={26} />
            <YAxis tick={{ fontSize: 10, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} tickFormatter={(v) => `${v}%`} width={40} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} formatter={(v, n) => [`${v?.toFixed(1)}%`, n]} />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: 1 }} />
            <ReferenceLine y={TARGET} stroke="#52525B" strokeDasharray="4 3" label={{ value: "TGT 2.0%", position: "right", fill: "#71717A", fontSize: 9 }} />
            {series.map((s) => (
              <Line key={s.key} name={s.label} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function InflationPage() {
  const qc = useQueryClient();
  const [refreshingRead, setRefreshingRead] = useState(false);
  const q = useQuery({ queryKey: ["inflation"], queryFn: fetchInflation, staleTime: 1000 * 60 * 60 });
  const readQ = useQuery({ queryKey: ["inflation-read"], queryFn: () => fetchInflationRead(false), staleTime: 1000 * 60 * 60, retry: false });
  const economies = q.data?.economies || [];

  const refreshRead = async () => {
    setRefreshingRead(true);
    try {
      qc.setQueryData(["inflation-read"], await fetchInflationRead(true));
    } catch {
      /* keep prior read */
    } finally {
      setRefreshingRead(false);
    }
  };
  const readBusy = readQ.isFetching || refreshingRead;

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="inflation-page">
      <div className="border-b border-zinc-800 px-4 py-2.5 flex items-center gap-3 sticky top-0 bg-black z-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-amber-term transition-colors" data-testid="inflation-back">
          <ArrowLeft size={13} strokeWidth={1.5} /> Terminal
        </Link>
        <span className="text-zinc-700">/</span>
        <span className="text-[13px] font-semibold text-zinc-100">Inflation Monitor</span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600 ml-auto hidden sm:inline">
          CPI · Core · PPI · 24M · {q.data?.sources || ""}
        </span>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* AI Inflation Read */}
        <div className="border border-amber-500/30 bg-[#09090B]" data-testid="inflation-ai-read">
          <div className="px-4 py-2 border-b border-amber-500/20 flex items-center gap-2">
            <Sparkles size={13} strokeWidth={1.5} className="text-amber-term" />
            <span className="text-[11px] uppercase tracking-widest text-zinc-300">Global Inflation Read · Claude Sonnet 4.5 · Live News</span>
            {readQ.data?.regime && (
              <span className="ml-auto text-[11px] uppercase tracking-widest text-amber-term border border-amber-500/40 px-2 py-0.5" data-testid="inflation-regime">
                {readQ.data.regime}
              </span>
            )}
            <button
              onClick={refreshRead}
              disabled={readBusy}
              data-testid="inflation-read-refresh"
              title="Refresh with the latest news & web search"
              className={`${readQ.data?.regime ? "" : "ml-auto"} text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50`}
            >
              <RefreshCw size={12} strokeWidth={1.5} className={readBusy ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="px-4 py-3">
            {readQ.isLoading ? (
              <p className="text-sm text-zinc-500 animate-pulse">Synthesizing the global inflation regime from CPI, Core & PPI across economies…</p>
            ) : readQ.isError ? (
              <p className="text-sm text-zinc-600">AI read unavailable right now — the underlying metrics below are still live.</p>
            ) : (
              <p className="text-sm text-zinc-100 leading-relaxed" data-testid="inflation-read-text">{readQ.data?.read}</p>
            )}
          </div>
        </div>

        {/* Comparison tool */}
        {economies.length > 0 && <ComparisonTool economies={economies} />}

        {/* Per-economy panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {economies.length
            ? economies.map((e) => <EconomyPanel key={e.code} e={e} />)
            : Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="border border-zinc-800 bg-[#09090B] h-[420px] animate-pulse" data-testid={`inflation-panel-skeleton-${i}`} />
              ))}
        </div>
      </div>
    </div>
  );
}
